import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  min2CharValidator,
  requiredValidator,
} from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import {
  IdealImageUpload,
  createIdeal,
  getIdealByID,
  updateIdeal,
} from "./services/ideal.services";
import { clearIdealDetails } from "./idealSlice";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import { getFile } from "../../_helper/helper";

const CreateIdeal: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const IdealID = location.state?.IdealID;
  const IdealDetail = useAppSelector((state) => state.ideal.IdealDetail);
  const loading = useAppSelector((state) => state.ideal.loading);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [IdealDetail]);

  useEffect(() => {
    if (IdealID) {
      dispatch(getIdealByID(IdealID));
    }
    return () => {
      dispatch(clearIdealDetails());
    };
  }, [IdealID]);

  const handleSubmit = async (values: any) => {
    const payload = {
      Title: values?.Title ? values?.Title : "",
      Description: values?.Description ? values?.Description : "",
      IsActive: values.IsActive === 1,
    };
    const ImageData = new FormData();
    const Images =
      values?.Images && typeof values?.Images !== "string"
        ? getFile(values?.Images)
        : "";

    try {
      if (IdealID) {
        const editPayload = {
          ...payload,
          ID: IdealID,
        };

        const response = await dispatch(updateIdeal(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (values?.Images && typeof values?.Images !== "string") {
            ImageData.append("Images", Images);
            ImageData.append("ID", IdealID);

            await dispatch(IdealImageUpload(ImageData));
          }
          navigate("/ideal");
          dispatch(clearIdealDetails());
        }
      } else {
        const response = await dispatch(createIdeal(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            values?.Images &&
            typeof values?.Images !== "string" &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("Images", Images);
            ImageData.append("ID", response?.payload?.Data?.ID);
            await dispatch(IdealImageUpload(ImageData));
          }
          navigate("/ideal");
          dispatch(clearIdealDetails());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  // if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          onSubmit={handleSubmit}
          key={formKey}
          initialValues={IdealDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 20 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "33.33%" },
                    { width: "33.33%" },
                    { width: "33.33%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>
                      {IdealID ? "Update Ideal" : "Add Ideal"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="Title"
                      label="Title"
                      placeholder="Title"
                      component={FormTextField}
                      validator={min2CharValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="Description"
                      label="Description"
                      component={FormRichTextField}
                      // validator={requiredValidator}
                      // astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem rowSpan={2}>
                    <Field
                      justifyContent="start"
                      name={`Images`}
                      label="Image"
                      type="file"
                      alternateName="Image"
                      accept="image/*"
                      component={FormImageUpload}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`IsActive`}
                      label="Status"
                      placeholder="i.e. Active"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={2}
                    style={{
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={IdealID ? "Update" : "Save"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/ideal")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateIdeal;
